import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { formatCurrency } from '../lib/utils';
import { Wifi, WifiOff, Send, Trash2, Bell } from 'lucide-react';

export default function WebSocketTest() {
  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [channel, setChannel] = useState('general');
  const wsRef = useRef(null);

  const connect = () => {
    const wsUrl = `${process.env.REACT_APP_BACKEND_URL?.replace('https', 'wss').replace('http', 'ws').replace('/api', '')}/ws/${channel}`;
    
    console.log('Connecting to:', wsUrl);
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        setConnected(true);
        addMessage('system', '🟢 Conectado ao WebSocket!');
        // Send ping
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          addMessage(data.type, data);
        } catch (e) {
          addMessage('raw', event.data);
        }
      };
      
      wsRef.current.onclose = () => {
        setConnected(false);
        addMessage('system', '🔴 Desconectado');
      };
      
      wsRef.current.onerror = (error) => {
        addMessage('error', `❌ Erro: ${error.message || 'Erro de conexão'}`);
      };
    } catch (error) {
      addMessage('error', `❌ Erro ao conectar: ${error.message}`);
    }
  };

  const disconnect = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  const addMessage = (type, data) => {
    const msg = {
      id: Date.now(),
      type,
      data,
      timestamp: new Date().toLocaleTimeString('pt-BR'),
    };
    setMessages(prev => [msg, ...prev].slice(0, 100));
  };

  const clearMessages = () => {
    setMessages([]);
  };

  const sendPing = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
      addMessage('sent', { type: 'ping' });
    }
  };

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const getMessageStyle = (type) => {
    switch (type) {
      case 'new_sale':
        return 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400';
      case 'new_order':
        return 'bg-blue-500/10 border-blue-500/30 text-blue-400';
      case 'pong':
        return 'bg-purple-500/10 border-purple-500/30 text-purple-400';
      case 'system':
        return 'bg-muted text-muted-foreground';
      case 'error':
        return 'bg-destructive/10 border-destructive/30 text-destructive';
      case 'sent':
        return 'bg-primary/10 border-primary/30 text-primary';
      default:
        return 'bg-muted';
    }
  };

  return (
    <div className="space-y-6" data-testid="websocket-test-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Teste WebSocket</h1>
          <p className="text-muted-foreground">Teste as notificações em tempo real</p>
        </div>
        <Badge variant={connected ? 'default' : 'secondary'} className="text-sm px-3 py-1">
          {connected ? (
            <>
              <Wifi className="h-4 w-4 mr-2" />
              Conectado
            </>
          ) : (
            <>
              <WifiOff className="h-4 w-4 mr-2" />
              Desconectado
            </>
          )}
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Conexão</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm text-muted-foreground mb-1 block">Canal</label>
              <Input
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                placeholder="general"
                disabled={connected}
              />
            </div>
            {!connected ? (
              <Button onClick={connect} data-testid="ws-connect-btn">
                <Wifi className="h-4 w-4 mr-2" />
                Conectar
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={sendPing}>
                  <Send className="h-4 w-4 mr-2" />
                  Ping
                </Button>
                <Button variant="destructive" onClick={disconnect}>
                  <WifiOff className="h-4 w-4 mr-2" />
                  Desconectar
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Mensagens ({messages.length})
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={clearMessages}>
            <Trash2 className="h-4 w-4 mr-1" />
            Limpar
          </Button>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] pr-4">
            {messages.length === 0 ? (
              <div className="text-center text-muted-foreground py-12">
                <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Nenhuma mensagem ainda</p>
                <p className="text-sm mt-1">Conecte e faça uma venda no PDV para ver notificações</p>
              </div>
            ) : (
              <div className="space-y-2">
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`p-3 rounded-lg border ${getMessageStyle(msg.type)}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="outline" className="text-xs">
                        {msg.type}
                      </Badge>
                      <span className="text-xs opacity-60">{msg.timestamp}</span>
                    </div>
                    <div className="text-sm font-mono">
                      {typeof msg.data === 'string' ? (
                        msg.data
                      ) : msg.type === 'new_sale' ? (
                        <div>
                          <p>💰 Nova venda: <strong>{formatCurrency(msg.data.data?.total || 0)}</strong></p>
                          <p className="text-xs opacity-70">Por: {msg.data.data?.user_name}</p>
                        </div>
                      ) : (
                        <pre className="text-xs overflow-auto">
                          {JSON.stringify(msg.data, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Como testar</CardTitle>
        </CardHeader>
        <CardContent className="prose prose-sm dark:prose-invert max-w-none">
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Clique em <strong>Conectar</strong> para estabelecer a conexão WebSocket</li>
            <li>Abra o <strong>PDV</strong> em outra aba (selecione um caixa aberto)</li>
            <li>Faça uma venda completa (adicione itens, selecione pagamento, finalize)</li>
            <li>Volte aqui e veja a notificação <code>new_sale</code> aparecer em tempo real!</li>
          </ol>
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground">
              <strong>Tipos de notificações:</strong> new_sale, new_order, order_updated, table_status, low_stock
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
